import { isPast, isToday } from 'date-fns';
import { createReducer } from 'typesafe-actions';

import { PHONE_VALIDATION_REG_EX } from 'shared/constants/form/validation';
import {
  ECoworkingOfferType,
  ERentDurationUnit,
} from 'shared/repositories/coworking/entities/api/V1BookCoworkingRequestSchema';

import {
  setCoworkingOfferTypeValueAction,
  setIsBrokerFieldValueAction,
  setNameFieldValueAction,
  setPhoneFieldValueAction,
  setRentDurationFieldValueAction,
  setRentDurationUnitFieldValueAction,
  setRentStartDateFieldValueAction,
  setWorkplaceCountFieldValueAction,
  validateAllAction,
  validateByFieldAction,
} from './actions';
import { EBookCoworkingFormFields } from './constants';
import {
  TBookCoworkingFormActionsType,
  TBookCoworkingFormReducer,
  TBookCoworkingFormState,
  TValidationRules,
} from './types';

export const getBookCoworkingFormInitialState = (
  fields?: Partial<TBookCoworkingFormState>,
): TBookCoworkingFormState => ({
  [EBookCoworkingFormFields.Phone]: { errorMessage: null, value: '' },
  [EBookCoworkingFormFields.CoworkingOfferType]: { errorMessage: null, value: ECoworkingOfferType.Office },
  [EBookCoworkingFormFields.Name]: { errorMessage: null, value: '' },
  [EBookCoworkingFormFields.RentStartDate]: { errorMessage: null, value: null },
  [EBookCoworkingFormFields.RentDuration]: { errorMessage: null, value: '' },
  [EBookCoworkingFormFields.IsBroker]: { errorMessage: null, value: false },
  [EBookCoworkingFormFields.RentDurationUnit]: { errorMessage: null, value: ERentDurationUnit.Month },
  [EBookCoworkingFormFields.WorkplaceCount]: { errorMessage: null, value: '' },
  ...fields,
});

export const validationRules: TValidationRules = {
  [EBookCoworkingFormFields.Phone]: (value: string) => {
    if (value.length === 0) {
      return 'Введите номер телефона';
    }

    if (PHONE_VALIDATION_REG_EX.test(value)) {
      return null;
    }

    return 'Введите корректный номер телефона';
  },
  [EBookCoworkingFormFields.IsBroker]: () => null,
  [EBookCoworkingFormFields.RentStartDate]: (value: Date | null) => {
    if (!value) {
      return 'Введите дату начала аренды';
    }

    if (isPast(value) && !isToday(value)) {
      return 'Нельзя указывать прошлую дату';
    }

    return null;
  },
  [EBookCoworkingFormFields.Name]: (value: string) => {
    if (value.length === 0) {
      return 'Введите ваше имя';
    }

    if (value.length > 200) {
      return 'Максимальное количество символов – 200';
    }

    return null;
  },
  [EBookCoworkingFormFields.CoworkingOfferType]: () => null,
  [EBookCoworkingFormFields.RentDuration]: (value: string) => {
    const intValue = parseInt(value, 10);

    if (isNaN(intValue)) {
      return 'Введите срок аренды';
    }

    if (intValue > 2147483647) {
      return 'Слишком большое число';
    }

    if (intValue < 1) {
      return 'Минимальное число – 1';
    }

    return null;
  },
  [EBookCoworkingFormFields.RentDurationUnit]: () => null,
  [EBookCoworkingFormFields.WorkplaceCount]: (value: string) => {
    const intValue = parseInt(value, 10);

    if (isNaN(intValue)) {
      return 'Введите количество рабочих мест';
    }

    if (intValue > 2147483647) {
      return 'Слишком большое число';
    }

    if (intValue < 1) {
      return 'Минимальное число – 1';
    }

    return null;
  },
};

export const validationRulesKeys = Object.keys(validationRules) as Array<EBookCoworkingFormFields>;

const bookCoworkingFormBaseReducer = createReducer<TBookCoworkingFormState, TBookCoworkingFormActionsType>(
  getBookCoworkingFormInitialState(),
)
  .handleAction(
    [
      setPhoneFieldValueAction,
      setNameFieldValueAction,
      setRentDurationFieldValueAction,
      setRentStartDateFieldValueAction,
      setWorkplaceCountFieldValueAction,
      setIsBrokerFieldValueAction,
      setRentDurationUnitFieldValueAction,
      setCoworkingOfferTypeValueAction,
    ],
    (state, action) => {
      state[action.type].value = action.payload;
      state[action.type].errorMessage = null;

      return { ...state };
    },
  )
  .handleAction(validateByFieldAction, (state, action) => {
    const validationRule = validationRules[action.payload];
    const fieldValue = state[action.payload].value;

    state[action.payload].errorMessage = validationRule(fieldValue, state);

    return { ...state };
  })
  .handleAction(validateAllAction, state => {
    validationRulesKeys.forEach(key => {
      state[key].errorMessage = validationRules[key](state[key].value, state);
    });

    return { ...state };
  });

export const bookCoworkingFormReducer: TBookCoworkingFormReducer = (state, action) =>
  bookCoworkingFormBaseReducer.handlers[action.type](state, action);
